
import Image from "next/image";
import Link from "next/link";
import { cn, getArtworkImageUrl, shouldBypassNextImageOptimization } from "@/lib/utils";
import type { ArtItem, CollectionItemDetails } from "@/types";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { EyeOff, ArchiveRestore } from "lucide-react";
import { useMemo } from "react";

interface ArtCardProps {
  item: ArtItem & Partial<Pick<CollectionItemDetails, "archived">>;
  priority?: boolean;
  prefetchLink?: boolean;
  showPrice?: boolean;
}

export function ArtCard({ item, priority = false, prefetchLink = false, showPrice = true }: ArtCardProps) {
  const isArchived = !!item.archived;
  const isPrivate = item.visibility === "private" && !isArchived;

  const imageUrl = useMemo(() => {
    return getArtworkImageUrl(item, 'md');
  }, [item]);

  const priceFormatted = useMemo(() => {
    if (!item.price || item.price <= 0) return null;
    const currency = item.currency || "USD";
    try {
      return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(item.price / 100);
    } catch {
      // Fallback if currency code is unexpected
      return `$${(item.price / 100).toFixed(2)}`;
    }
  }, [item.price, item.currency]);

  const altText = useMemo(() => {
    const artists = item.artistNames?.join(", ");
    const bits = [item.title, artists, item.year].filter(Boolean);
    return bits.join(" – ");
  }, [item.title, item.artistNames, item.year]);

  const tags = item.searchTerms || [];

  return (
    <Card
      // Make the entire card a link via asChild to avoid nested interactive elements
      className={cn(
        "flex flex-col h-full transition-all duration-300 ease-in-out",
        "group hover:shadow-lg hover:-translate-y-1",
        "motion-reduce:transition-none motion-reduce:hover:shadow-md motion-reduce:hover:-translate-y-0"
      )}
      asChild
    >
      <Link href={`/artworks/${item.id}`} prefetch={prefetchLink}>
        <div className="relative">
          <CardHeader className="p-0">
            <div className={cn("aspect-[3/4] relative overflow-hidden rounded-t-lg", isArchived && "grayscale")}>
              {imageUrl ? (
                <Image
                  src={imageUrl}
                  alt={altText}
                  data-ai-hint={`${tags[0] || ""} ${tags[1] || ""}`}
                  fill
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 25vw"
                  priority={priority}
                  loading={priority ? "eager" : "lazy"}
                  className="object-cover"
                  unoptimized={shouldBypassNextImageOptimization(imageUrl)}
                  // If you have blurDataURL in your data model, pass it here:
                  // placeholder="blur"
                  // blurDataURL={item.blurDataURL}
                />
              ) : (
                <div className="absolute inset-0 grid place-items-center bg-muted">
                  <span className="text-sm text-muted-foreground">No image</span>
                </div>
              )}

              {isArchived && (
                <div
                  className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center text-center text-white p-4"
                  aria-label="Archived artwork"
                >
                  <ArchiveRestore className="h-10 w-10 mb-2" />
                  <h3 className="font-bold">Archived</h3>
                </div>
              )}
            </div>
          </CardHeader>

          {isPrivate && (
            <div className="absolute top-2 right-2 z-10">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className="bg-yellow-400/80 text-background p-1.5 rounded-full backdrop-blur-sm cursor-default"
                      aria-label="Visible only to you"
                    >
                      <EyeOff className="h-4 w-4" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Visible only to you</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <span className="sr-only">Private artwork</span>
            </div>
          )}
        </div>

        <CardContent className="p-4 flex-1">
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="font-headline text-xl mb-1">
              {item.title}
              {item.year ? ` (${item.year})` : ""}
            </CardTitle>
            {isPrivate && <Badge variant="secondary">Private</Badge>}
          </div>
          <CardDescription className="text-sm text-muted-foreground mb-2">
            {item.artistNames?.join(", ")}
          </CardDescription>
        </CardContent>

        <CardFooter className="p-4 pt-0 flex justify-between items-center">
          <div />
          {showPrice && priceFormatted && <p className="text-lg font-semibold text-primary">{priceFormatted}</p>}
        </CardFooter>
      </Link>
    </Card>
  );
}
